import { ClubAwardPlaces, givenClubAwards } from '@/components/club-awards'
import { HeroWinner } from '@/components/hero-award'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DownloadIcon } from 'lucide-react'

import { useCurrentShow } from '@/context/current-show'
import { SelectShowNotice } from '@/components/current-show'
import { ReportLayout } from '@/layouts/ReportLayout'
import { Button } from '@/components/ui/button'
import { triggerReportPrint } from '@/lib/print'
import { parseDateOnlyToLocal } from '@/lib/date'
import {
  buildDivisionWinners,
  formatClassDisplayName,
  formatDivisionDisplayName,
  getPlacementParts,
  orderPlacementColumns,
  renderAwardWinner,
} from '@/lib/report-results'
import { useReportResults, useReportSummary, useReportAwards } from '@/api/hooks'

import type {
  ReportAwardsOutput,
  ReportDivisionResultsOutput,
  ReportResultsOutput,
  ReportSummaryOutput,
} from '@schemas'

import '@/styles/display-results-report.css'

type DisplayPage = {
  divisionId: string
  divisionName: string
  divisionCode: string | null
  classes: ReportDivisionResultsOutput['classes']
}

export function buildDisplayPages(results: ReportResultsOutput | undefined): DisplayPage[] {
  if (!results) return []

  const pages: DisplayPage[] = []

  for (const division of results.divisions) {
    const sortedClasses = [...division.classes].sort((a, b) => {
      const orderA = a.classOrder ?? Number.MAX_SAFE_INTEGER
      const orderB = b.classOrder ?? Number.MAX_SAFE_INTEGER
      if (orderA !== orderB) return orderA - orderB

      const codeA = a.classCode ?? ''
      const codeB = b.classCode ?? ''
      if (codeA !== codeB) return codeA.localeCompare(codeB)

      return a.className.localeCompare(b.className)
    })

    for (let index = 0; index < sortedClasses.length; index += 2) {
      const slice = sortedClasses.slice(index, index + 2)
      if (slice.length === 0) continue
      pages.push({
        divisionId: division.divisionId,
        divisionName: division.divisionName,
        divisionCode: division.divisionCode ?? null,
        classes: slice,
      })
    }
  }

  return pages
}

function formatDateRange(showStartDate?: string, showEndDate?: string): string | null {
  const start = parseDateOnlyToLocal(showStartDate)
  const end = parseDateOnlyToLocal(showEndDate)
  if (!start || !end) return null

  const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
  return `${formatter.format(start)} – ${formatter.format(end)}`
}

function formatTimestamp(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export default function DisplayResultsReportPage() {
  const { currentShow } = useCurrentShow()
  const [searchParams] = useSearchParams()
  const showIdParam = searchParams.get('showId') ?? undefined
  const showNameParam = searchParams.get('showName') ?? undefined
  const showLocationParam = searchParams.get('showLocation') ?? undefined
  const showStartDateParam = searchParams.get('showStartDate') ?? undefined
  const showEndDateParam = searchParams.get('showEndDate') ?? undefined

  const showId = currentShow?.id ?? showIdParam
  const showName = currentShow?.name ?? showNameParam ?? 'Selected Show'
  const showLocation = currentShow?.location ?? showLocationParam ?? undefined
  const showStartDate = currentShow?.startDate ?? showStartDateParam ?? undefined
  const showEndDate = currentShow?.endDate ?? showEndDateParam ?? undefined

  const summaryQuery = useReportSummary(showId)
  const resultsQuery = useReportResults(showId)
  const awardsQuery = useReportAwards(showId)

  const isLoading = summaryQuery.isLoading || resultsQuery.isLoading || awardsQuery.isLoading
  const error = summaryQuery.error ?? resultsQuery.error ?? awardsQuery.error ?? null

  const summary: ReportSummaryOutput | undefined = summaryQuery.data
  const results = resultsQuery.data
  const awards: ReportAwardsOutput | undefined = awardsQuery.data

  const pages = useMemo(() => buildDisplayPages(results), [results])
  const printedOn = useMemo(() => formatTimestamp(new Date()), [])
  const dateRange = useMemo(
    () => formatDateRange(showStartDate, showEndDate),
    [showEndDate, showStartDate]
  )
  const bestInShow = useMemo(() => renderAwardWinner(awards?.bestInShow), [awards?.bestInShow])
  const divisionWinners = useMemo(() => buildDivisionWinners(awards, results), [awards, results])
  const showDivisionAwards = awards?.bestInDivisionEnabled !== false
  const clubAwards = givenClubAwards(awards, showId, currentShow)

  if (!showId) {
    return <SelectShowNotice description="Choose a show to print the display results." />
  }

  const header = (
    <div className="space-y-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Show Results</p>
          <h1 className="text-3xl font-semibold leading-tight">{showName}</h1>
          {showLocation ? (
            <p className="text-muted-foreground text-sm">
              {showLocation}
              {dateRange ? ` • ${dateRange}` : ''}
            </p>
          ) : dateRange ? (
            <p className="text-muted-foreground text-sm">{dateRange}</p>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="print-hidden"
          onClick={triggerReportPrint}
        >
          <DownloadIcon className="mr-2 h-4 w-4" aria-hidden="true" />
          Print Layout
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Prepared {printedOn}
        {dateRange ? ` • ${dateRange}` : ''}
      </p>
    </div>
  )

  if (isLoading) {
    return (
      <ReportLayout header={header} showWatermark={false}>
        <p className="text-muted-foreground text-base">Loading show results…</p>
      </ReportLayout>
    )
  }

  if (error) {
    return (
      <ReportLayout header={header} showWatermark={false}>
        <p className="text-destructive text-base">
          {error.message || 'Unable to load display results.'}
        </p>
      </ReportLayout>
    )
  }

  if (!summary || !results) {
    return (
      <ReportLayout header={header} showWatermark={false}>
        <p className="text-destructive text-base">Display results were not available.</p>
      </ReportLayout>
    )
  }

  if (pages.length === 0) {
    return (
      <ReportLayout header={header} showWatermark={false}>
        <p className="text-muted-foreground text-base">No judging results have been recorded.</p>
      </ReportLayout>
    )
  }

  return (
    <ReportLayout header={header} showWatermark={false}>
      <div className="display-report">
        {pages.map((page, pageIndex) => (
          <section
            key={`${page.divisionId}-${page.classes[0]?.classId ?? 'page'}`}
            className="display-report__page"
          >
            <header className="display-report__page-header report-layout__section-heading">
              <span className="display-report__division-label">Division</span>
              <h2 className="display-report__division-title">
                {formatDivisionDisplayName(page.divisionName, page.divisionCode)}
              </h2>
            </header>
            <div className="display-report__classes">
              {page.classes.map(showClass => {
                const placementColumns = orderPlacementColumns(showClass.placements, {
                  placeCount: showClass.placeCount,
                })
                const classDisplayName = formatClassDisplayName(
                  showClass.className,
                  showClass.classCode
                )
                return (
                  <article key={showClass.classId} className="display-report__class-card">
                    <header className="display-report__class-header">
                      <h3 className="display-report__class-name">{classDisplayName}</h3>
                    </header>
                    {placementColumns.length === 0 ? (
                      <p className="text-lg text-muted-foreground">No placements recorded.</p>
                    ) : (
                      <div className="display-report__placement-list">
                        {placementColumns.map(column => (
                          <div key={column.label} className="display-report__placement-item">
                            {column.entries.length === 0
                              ? (() => {
                                  const { base, suffix } = getPlacementParts(column.label)
                                  return (
                                    <div
                                      className="display-report__entry-line"
                                      key={`${column.label}-placeholder`}
                                    >
                                      <span className="display-report__placement-label">
                                        <span>{base}</span>
                                        {suffix ? <sup>{suffix}</sup> : null}
                                      </span>
                                      <span className="display-report__placement-separator">-</span>
                                      <span className="display-report__entry-name">-</span>
                                    </div>
                                  )
                                })()
                              : column.entries.map((entry, entryIndex) => {
                                  const exhibitor =
                                    entry.exhibitorName?.trim() || 'Unknown exhibitor'
                                  const key = entry.exhibitId
                                    ? `${column.label}-${entry.exhibitId}`
                                    : `${column.label}-${entry.exhibitorId ?? exhibitor}-${entryIndex}`
                                  const { base, suffix } = getPlacementParts(column.label)
                                  return (
                                    <div key={key} className="display-report__entry-line">
                                      <span className="display-report__placement-label">
                                        <span>{base}</span>
                                        {suffix ? <sup>{suffix}</sup> : null}
                                      </span>
                                      <span className="display-report__placement-separator">-</span>
                                      <span className="display-report__entry-name">
                                        {exhibitor}
                                      </span>
                                    </div>
                                  )
                                })}
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
            {pageIndex === 0 ? (
              <div className="display-report__meta space-y-2">
                <p>
                  <span className="font-semibold">Best in Show:</span> {bestInShow}
                </p>
                {awards?.sweepstakes && (
                  <p>
                    <span className="font-semibold">Grand Champion:</span>{' '}
                    {renderAwardWinner(awards.sweepstakes)}
                  </p>
                )}
                {(awards?.reserveGrands ?? []).map((winner, index) => (
                  <p key={winner.awardId ?? index}>
                    <span className="font-semibold">Reserve Grand Champion:</span>{' '}
                    {renderAwardWinner(winner)}
                  </p>
                ))}

                <HeroWinner state={awards?.heroAward} />
                {clubAwards.map(({ title, measure, award }) => (
                  <div key={title} className="space-y-1 text-sm leading-relaxed">
                    <p className="font-semibold">{title}</p>
                    <ClubAwardPlaces award={award} measure={measure} />
                  </div>
                ))}
                {showDivisionAwards ? (
                  divisionWinners.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No division champions have been identified.
                    </p>
                  ) : (
                    <div className="space-y-1 text-sm leading-relaxed">
                      <p className="font-semibold">Best in Division</p>
                      <ul className="space-y-1">
                        {divisionWinners.map(item => {
                          const divisionDisplayName = formatDivisionDisplayName(
                            item.divisionName,
                            item.divisionCode
                          )
                          return (
                            <li key={item.divisionId}>
                              <span className="font-medium">{divisionDisplayName}:</span>{' '}
                              {renderAwardWinner(item.winner)}
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )
                ) : null}
              </div>
            ) : null}
          </section>
        ))}
      </div>
    </ReportLayout>
  )
}
